import React, { useState, useEffect, useMemo } from "react";
import Modal from "./Modal";
import FlightDetails from "./FlightChart";
import FlightCalendar from "./FlightCalendar";
import { API_ENDPOINTS } from "../config";

import {
  Container,
  Row,
  Col,
  FormGroup,
  Input,
  Button
} from "reactstrap";

import { FaRegCalendarAlt } from 'react-icons/fa';


import "bootstrap/dist/css/bootstrap.min.css";
import "./FlightOffers.css";

const FlightOffers = () => {
  const [offers, setOffers] = useState([]);
  const [offersAll, setOffersAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenCalendar, setIsModalOpenCalendar] = useState(false);

  const [selectedFlight, setSelectedFlight] = useState({});
  const [isError, setIsError] = useState(false);

  const [isOnWawOnly, setIsOnWawOnly] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const handleToggleWawOnly = () => setIsOnWawOnly(!isOnWawOnly);

  const fetchSyncInfo = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.FLIGHTS_SYNC_INFO);
      const data = await response.json();

      if (data.status === "success" && data.data.last_sync) {
        setLastSync(new Date(data.data.last_sync));
      }
    } catch (error) {
      console.error("Błąd pobierania informacji o synchronizacji:", error);
    }
  };

  const fetchFlightOffers = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.FLIGHTS}?type=active`);
      const data = await response.json();

      if (data.status === "success") {
        setOffers(data.data);
        setIsError(false);
      } else {
        console.error("Błąd:", data.message);
        setIsError(true);
      }
    } catch (error) {
      console.error("Błąd sieci:", error);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchFlightOffersAll = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.FLIGHTS}?type=all`);
      const data = await response.json();

      if (data.status === "success") {
        setOffersAll(data.data);
      } else {
        console.error("Błąd:", data.message);
      }
    } catch (error) {
      console.error("Błąd sieci:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlightOffers();
    fetchFlightOffersAll();
    fetchSyncInfo();
    const intervalId = setInterval(() => {
      fetchFlightOffers();
      fetchSyncInfo();
    }, 60000);
    return () => clearInterval(intervalId);
  }, []);

  // Filtruj loty z przeszłych dat i WAW - PRZED early returns
  const flightOffers = offers;
  const flightOffersAll = offersAll;

  const filteredOffers = useMemo(() => {
    let filtered = flightOffers;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    filtered = filtered.filter((flight) => {
      if (!flight.dates) {
        return true;
      }

      const flightDate = new Date(flight.dates);
      if (Number.isNaN(flightDate.getTime())) {
        return true;
      }

      flightDate.setHours(0, 0, 0, 0);
      return flightDate >= today;
    });

    if (isOnWawOnly) {
      filtered = filtered.filter((flight) => flight.airports.includes("WAW-"));
    }

    return filtered;
  }, [flightOffers, isOnWawOnly]);

  const sortedOffers = useMemo(() => {
    return [...filteredOffers].sort((a, b) => parseInt(a.price) - parseInt(b.price));
  }, [filteredOffers]);

  if (loading) {
    return <div>Ładowanie...</div>;
  }
  if (isError) {
    return <div>Błąd...</div>;
  }



  const openModal = (flight) => {
    setIsModalOpen(true);
    setSelectedFlight(flight);
  };

  const openModalCalendar = () => {
    setIsModalOpenCalendar(true);
  };

  const closeModal = () => setIsModalOpen(false);
  const closeModalCalendar = () => setIsModalOpenCalendar(false);

  return (
    <div className="flights-container">
      <div className="flights-header">
        <div className="filters-bar">
          <div className="filter-group">
            <label className="filter-label">
              <span className="filter-icon">✈️</span>
              <span className="filter-text">Tylko WAW</span>
              <FormGroup switch className="custom-switch">
                <Input
                  type="switch"
                  role="switch"
                  checked={isOnWawOnly}
                  onChange={handleToggleWawOnly}
                />
              </FormGroup>
            </label>
          </div>
          <Button
            className="calendar-btn"
            onClick={openModalCalendar}
          >
            <FaRegCalendarAlt className="calendar-icon" />
            <span>Kalendarz</span>
          </Button>
        </div>
      </div>

      <Container fluid className="offers-grid-container">
        <Modal isOpen={isModalOpen} onClose={closeModal}>
          <FlightDetails data={flightOffersAll} selectedFlight={selectedFlight} />
        </Modal>
        <Modal isOpen={isModalOpenCalendar} onClose={closeModalCalendar}>
          <FlightCalendar data={sortedOffers} onSelectFlight={(flight) => {
            setSelectedFlight(flight);
            setIsModalOpen(true);
            setIsModalOpenCalendar(false);
          }} />
        </Modal>

        <div className="section-header">
          <h2 className="section-title">Najlepsze oferty</h2>
          <p className="section-subtitle">{sortedOffers.length} aktywnych lotów</p>
        </div>

        <Row className="offers-grid">
          {sortedOffers.map((flight) => (
            <Col xs="12" sm="6" md="4" lg="3" xl="2" key={flight.id} className="offer-col">
              <div className="flight-card" onClick={() => openModal(flight)}>
                <div className="flight-card-header">
                  <div className="flight-badge-group">
                    <span className="flight-badge provider-badge">{flight.provider}</span>
                    {flight.brand !== "TUI" && (
                      <span className="flight-badge brand-badge">{flight.brand}</span>
                    )}
                  </div>
                </div>

                <div className="flight-card-body">
                  <h3 className="flight-destination">{flight.name}</h3>
                  <div className="flight-route-container">
                    <p className="flight-country">{flight.country}</p>
                    <div className="route-badge">
                      <span className="airport-code">{flight.airports.split('-')[0]}</span>
                      <span className="route-arrow">→</span>
                      <span className="airport-code">{flight.airports.split('-')[1]}</span>
                    </div>
                  </div>

                  <div className="flight-info">
                    <div className="flight-price">
                      <span className="price-label">od</span>
                      <span className="price-value">{flight.price}</span>
                      <span className="price-currency">zł</span>
                    </div>

                    {flight.dates && (
                      <div className="flight-date">
                        <span className="date-icon">📅</span>
                        <span className="date-value">{flight.dates}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flight-card-footer">
                  <button className="view-details-btn" onClick={(e) => {
                    e.stopPropagation();
                    openModal(flight);
                  }}>
                    Zobacz szczegóły
                    <span className="arrow-icon">→</span>
                  </button>
                  {flight.detail_url && (
                    <a
                      href={flight.detail_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="open-offer-btn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Otwórz ofertę
                      <span className="arrow-icon">↗</span>
                    </a>
                  )}
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {lastSync && (
          <div className="sync-info" style={{
            textAlign: 'center',
            padding: '20px',
            marginTop: '20px',
            color: '#666',
            fontSize: '14px',
            borderTop: '1px solid #eee'
          }}>
            Ostatnia synchronizacja: {lastSync.toLocaleString('pl-PL', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        )}

      </Container>
    </div>
  );
};

export default FlightOffers;
